import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../services/user.service';
import { AccountInformation, CompanyInformation, DealerInformation, SecurityInformation, UserProfile } from '../types/user.types';

/**
 * Hook for fetching the current user's profile data
 */
export const useUserProfile = () => {
  return useQuery({
    queryKey: ['userProfile'],
    queryFn: () => userService.getCurrentUser(),
  });
};

/**
 * Hook for updating current user data
 */
export const useUpdateCurrentUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (userData: Partial<UserProfile>) => 
      userService.updateCurrentUser(userData),
    onSuccess: () => {
      // Invalidate the userProfile query to refetch the updated data
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
    },
  });
};

/**
 * Hook for updating account information
 */
export const useUpdateAccountInfo = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (accountInfo: Partial<AccountInformation>) => 
      userService.updateAccountInfo(accountInfo),
    onSuccess: () => {
      // Invalidate the userProfile query to refetch the updated data
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
    },
  });
};

/**
 * Hook for updating company information
 */
export const useUpdateCompanyInfo = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (companyInfo: Partial<CompanyInformation>) => 
      userService.updateCompanyInfo(companyInfo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
    }
  });
};

/**
 * Hook for updating dealer information
 */
export const useUpdateDealerInfo = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (dealerInfo: Partial<DealerInformation>) => 
      userService.updateDealerInfo(dealerInfo),
    onMutate: async (newDealerInfo) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['userProfile'] });
      
      // Snapshot the previous value
      const previousData = queryClient.getQueryData<UserProfile>(['userProfile']);
      
      // Optimistically update to the new value
      if (previousData) {
        const updatedData = {
          ...previousData,
          dealerInformations: {
            ...previousData.dealerInformations || {},
            ...newDealerInfo
          }
        };
        queryClient.setQueryData<UserProfile>(['userProfile'], updatedData);
      }
      
      return { previousData };
    },
    onError: (_err, _newDealerInfo, context) => {
      // If the mutation fails, roll back to the previous value
      if (context?.previousData) {
        queryClient.setQueryData(['userProfile'], context.previousData);
      }
    },
    onSuccess: (data) => {
      // Update the cache with the response from the server
      const currentData = queryClient.getQueryData<UserProfile>(['userProfile']);
      if (currentData) {
        const updatedData = {
          ...currentData,
          dealerInformations: {
            ...currentData.dealerInformations || {},
            ...data // Use the server response data
          }
        };
        queryClient.setQueryData<UserProfile>(['userProfile'], updatedData);
      }
    }
  });
};

/**
 * Hook for updating username
 */
export const useUpdateUsername = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (username: string) => 
      userService.updateUsername(username),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
    },
  });
};

/**
 * Hook for updating password
 */
export const useUpdatePassword = () => {
  return useMutation({
    mutationFn: ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => 
      userService.updatePassword(currentPassword, newPassword),
  });
};

/**
 * Hook for updating security information
 */
export const useUpdateSecurityInfo = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (securityInfo: Partial<SecurityInformation>) => 
      userService.updateSecurityInfo(securityInfo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
    },
  });
};
